import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Tag,
  message,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import AuthGuard from '../components/AuthGuard';
import { getOperationLogs } from '../services/adminApi';
import dayjs from 'dayjs';

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

interface OperationLog {
  id: number;
  userId: number | null;
  username: string | null;
  adminId: number | null;
  admin: {
    phone: string;
    nickname: string;
  };
  action: string;
  module: string;
  ip: string | null;
  userAgent: string | null;
  requestData: string | null;
  responseData: string | null;
  status: boolean;
  errorMessage: string | null;
  createdAt: string;
}

const OperationLogs: React.FC = () => {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [adminIdFilter, setAdminIdFilter] = useState<number | undefined>();
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [moduleFilter, setModuleFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 获取操作日志
  const fetchLogs = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await getOperationLogs({
        page,
        pageSize,
        adminId: adminIdFilter,
        action: actionFilter,
        module: moduleFilter,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
      });

      if (response.data.success) {
        setLogs(response.data.data.list);
        setPagination({
          current: response.data.data.page,
          pageSize: response.data.data.pageSize,
          total: response.data.data.total,
        });
      }
    } catch (error: any) {
      message.error(error.message || '获取操作日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [adminIdFilter, actionFilter, moduleFilter, dateRange]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '管理员',
      dataIndex: ['admin', 'nickname'],
      key: 'admin',
      render: (nickname: string, record: OperationLog) => (
        <div>
          <div>{nickname || '未知'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.admin?.phone || '-'}
          </div>
        </div>
      ),
    },
    {
      title: '操作类型',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => <Tag color="blue">{action}</Tag>,
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      render: (module: string) => <Tag>{module}</Tag>,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      render: (ip: string) => ip || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: boolean) => (
        <Tag color={status ? 'success' : 'error'}>
          {status ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (errorMessage: string) => errorMessage || '-',
    },
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '请求数据',
      dataIndex: 'requestData',
      key: 'requestData',
      render: (data: string) => (
        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data || '-'}
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    fetchLogs(pagination.current, pagination.pageSize);
  };

  return (
    <AuthGuard requireSuperAdmin>
      <div>
        <Card
          title="操作日志"
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => fetchLogs()}>
              刷新
            </Button>
          }
        >
          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Search
                  placeholder="搜索管理员ID"
                  onSearch={(value) => setAdminIdFilter(value ? parseInt(value) : undefined)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="筛选操作类型"
                  allowClear
                  value={actionFilter}
                  onChange={setActionFilter}
                  style={{ width: '100%' }}
                >
                  <Option value="CREATE_ADMIN">创建管理员</Option>
                  <Option value="UPDATE_ADMIN">更新管理员</Option>
                  <Option value="DELETE_ADMIN">删除管理员</Option>
                  <Option value="UPDATE_SYSTEM_CONFIG">修改系统配置</Option>
                  <Option value="UPDATE_APP_VERSION">修改APP版本</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="筛选模块"
                  allowClear
                  value={moduleFilter}
                  onChange={setModuleFilter}
                  style={{ width: '100%' }}
                >
                  <Option value="ADMIN_MANAGEMENT">管理员管理</Option>
                  <Option value="SYSTEM_CONFIG">系统配置</Option>
                  <Option value="APP_VERSION">APP版本</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  placeholder={['开始日期', '结束日期']}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </div>

          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </AuthGuard>
  );
};

export default OperationLogs;